import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  BarChart, 
  Bar,
  ScatterChart,
  Scatter,
  PieChart,
  Pie,
  Cell
} from 'recharts'
import { FraudRule } from '@/services/mockRules'
import { TrendingUp, TrendingDown, Target, AlertTriangle } from 'lucide-react'

interface RuleAnalyticsProps {
  rules: FraudRule[]
}

export function RuleAnalytics({ rules }: RuleAnalyticsProps) {
  // Generate mock analytics data
  const generateEffectivenessData = () => {
    const days = Array.from({ length: 30 }, (_, i) => {
      const date = new Date()
      date.setDate(date.getDate() - 29 + i)
      return {
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        ...rules.reduce((acc, rule) => {
          acc[rule.name] = Math.floor(Math.random() * 20) + 80 + (Math.sin((i + rule.priority) / 5) * 10)
          return acc
        }, {} as Record<string, number>)
      }
    })
    return days
  }

  const generateFalsePositiveData = () => {
    return rules.map(rule => ({
      name: rule.name.length > 15 ? rule.name.substring(0, 15) + '...' : rule.name,
      fullName: rule.name,
      falsePositives: rule.metrics.falsePositives,
      detections: rule.metrics.detections,
      rate: rule.metrics.detections > 0 ? (rule.metrics.falsePositives / rule.metrics.detections) * 100 : 0
    }))
  }

  const generateWeightAccuracyData = () => {
    return rules.map(rule => ({
      weight: rule.weight,
      accuracy: rule.metrics.accuracy,
      name: rule.name,
      category: rule.category,
      detections: rule.metrics.detections
    }))
  }

  const generateCategoryData = () => {
    const categories = rules.reduce((acc, rule) => {
      if (!acc[rule.category]) {
        acc[rule.category] = { 
          name: rule.category, 
          value: 0, 
          detections: 0,
          accuracy: 0,
          count: 0
        }
      }
      acc[rule.category].value += rule.weight
      acc[rule.category].detections += rule.metrics.detections
      acc[rule.category].accuracy += rule.metrics.accuracy
      acc[rule.category].count += 1
      return acc
    }, {} as Record<string, any>)

    return Object.values(categories).map((cat: any) => ({
      ...cat,
      accuracy: cat.accuracy / cat.count
    }))
  }

  const effectivenessData = generateEffectivenessData()
  const falsePositiveData = generateFalsePositiveData()
  const weightAccuracyData = generateWeightAccuracyData()
  const categoryData = generateCategoryData()

  const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4']

  const topPerformer = rules.reduce((best, rule) => 
    rule.metrics.accuracy > best.metrics.accuracy ? rule : best
  )

  const mostTriggered = rules.reduce((most, rule) => 
    rule.metrics.detections > most.metrics.detections ? rule : most
  )

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {Math.round(rules.reduce((sum, rule) => sum + rule.metrics.accuracy, 0) / rules.length)}%
                </div>
                <div className="text-sm text-muted-foreground">Avg Accuracy</div>
              </div>
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {rules.reduce((sum, rule) => sum + rule.metrics.detections, 0)}
                </div>
                <div className="text-sm text-muted-foreground">Total Detections</div>
              </div>
              <Target className="w-5 h-5 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-red-600">
                  {rules.reduce((sum, rule) => sum + rule.metrics.falsePositives, 0)}
                </div>
                <div className="text-sm text-muted-foreground">False Positives</div>
              </div>
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-purple-600">
                  {rules.filter(rule => rule.enabled).length}
                </div>
                <div className="text-sm text-muted-foreground">Active Rules</div>
              </div>
              <div className="w-2 h-2 rounded-full bg-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Performers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Top Performer</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">{topPerformer.name}</h3>
                <p className="text-sm text-muted-foreground">{topPerformer.description}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className="bg-green-100 text-green-800">
                    {topPerformer.metrics.accuracy}% Accuracy
                  </Badge>
                  <Badge variant="secondary">
                    {topPerformer.metrics.detections} Detections
                  </Badge>
                </div>
              </div>
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Most Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">{mostTriggered.name}</h3>
                <p className="text-sm text-muted-foreground">{mostTriggered.description}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className="bg-blue-100 text-blue-800">
                    {mostTriggered.metrics.detections} Detections
                  </Badge>
                  <Badge variant="secondary">
                    {mostTriggered.metrics.accuracy}% Accuracy
                  </Badge>
                </div>
              </div>
              <Target className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="effectiveness" className="space-y-4">
        <TabsList>
          <TabsTrigger value="effectiveness">Rule Effectiveness</TabsTrigger>
          <TabsTrigger value="false-positives">False Positives</TabsTrigger>
          <TabsTrigger value="weight-analysis">Weight Analysis</TabsTrigger>
          <TabsTrigger value="categories">Category Breakdown</TabsTrigger>
        </TabsList>

        <TabsContent value="effectiveness">
          <Card>
            <CardHeader>
              <CardTitle>Rule Effectiveness Over Time</CardTitle>
              <p className="text-sm text-muted-foreground">
                Accuracy percentage for each rule over the past 30 days
              </p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={effectivenessData}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis 
                    dataKey="date" 
                    fontSize={12}
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis 
                    domain={[70, 100]}
                    fontSize={12}
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px'
                    }}
                  />
                  {rules.slice(0, 4).map((rule, index) => (
                    <Line 
                      key={rule.id}
                      type="monotone" 
                      dataKey={rule.name} 
                      stroke={COLORS[index]} 
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="false-positives">
          <Card>
            <CardHeader>
              <CardTitle>False Positive Rate by Rule</CardTitle>
              <p className="text-sm text-muted-foreground">
                Comparison of false positive rates across all rules
              </p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={falsePositiveData}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis 
                    dataKey="name"
                    fontSize={12}
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    angle={-45}
                    textAnchor="end"
                    height={100}
                  />
                  <YAxis 
                    fontSize={12}
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px'
                    }}
                    formatter={(value: any, name: string, props: any) => [
                      name === 'rate' ? `${value.toFixed(1)}%` : value,
                      name === 'rate' ? 'False Positive Rate' : 
                      name === 'falsePositives' ? 'False Positives' : 'Total Detections'
                    ]}
                    labelFormatter={(label) => falsePositiveData.find(d => d.name === label)?.fullName || label}
                  />
                  <Bar dataKey="rate" fill="#ef4444" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="weight-analysis">
          <Card>
            <CardHeader>
              <CardTitle>Weight vs Accuracy Analysis</CardTitle>
              <p className="text-sm text-muted-foreground">
                Correlation between rule weight and accuracy performance
              </p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <ScatterChart data={weightAccuracyData}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis 
                    type="number"
                    dataKey="weight"
                    name="Weight"
                    fontSize={12}
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis 
                    type="number"
                    dataKey="accuracy"
                    name="Accuracy"
                    domain={[80, 100]}
                    fontSize={12}
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px'
                    }}
                    formatter={(value: any, name: string) => [
                      name === 'accuracy' ? `${value}%` : value,
                      name === 'accuracy' ? 'Accuracy' : 'Weight'
                    ]}
                    labelFormatter={(label, payload) => 
                      payload?.[0]?.payload?.name || 'Rule'
                    }
                  />
                  <Scatter dataKey="accuracy" fill="#3b82f6" />
                </ScatterChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categories">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Detection by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="detections"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Category Performance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {categoryData.map((category, index) => (
                  <div key={category.name} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="font-medium capitalize">{category.name}</span>
                      </div>
                      <Badge variant="secondary">
                        {Math.round(category.accuracy)}% avg
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {category.detections} detections • {category.count} rules
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}